import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Calha e número de colunas por faixa. Servidos como custom properties para que
 * o consumidor possa consumi-los em qualquer utilitário
 * (ex.: `gap-y-[var(--grid-gutter)]`).
 */
const densityVars = [
  '[--grid-gutter:var(--grid-portrait-gutter)]',
  'min-[576px]:[--grid-gutter:var(--grid-tablet-gutter)]',
  'min-[1280px]:[--grid-gutter:var(--grid-desktop-gutter)]',
  'min-[1600px]:[--grid-gutter:var(--grid-tv-gutter)]',
  '[--grid-columns:var(--grid-portrait-columns)]',
  'min-[576px]:[--grid-columns:var(--grid-tablet-columns)]',
  'min-[1280px]:[--grid-columns:var(--grid-desktop-columns)]',
  'min-[1600px]:[--grid-columns:var(--grid-tv-columns)]',
]

type ContainerProps = React.ComponentProps<'div'> & {
  /**
   * `.container-fluid` do DS: mantém as margens laterais da faixa, mas não
   * limita a largura — o conteúdo acompanha a viewport.
   */
  fluid?: boolean
  /** Renderiza no elemento filho (`<main>`, `<section>`…) em vez de `<div>`. */
  asChild?: boolean
}

function Container({ className, fluid = false, asChild = false, ...props }: ContainerProps) {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-slot="container"
      data-fluid={fluid ? '' : undefined}
      className={cn(
        'mx-auto w-full',
        // Margem lateral por faixa (`--grid-margin` é o nome que o próprio
        // core.css usa).
        '[--grid-margin:var(--grid-portrait-margin)]',
        'min-[576px]:[--grid-margin:var(--grid-tablet-margin)]',
        'min-[1280px]:[--grid-margin:var(--grid-desktop-margin)]',
        'min-[1600px]:[--grid-margin:var(--grid-tv-margin)]',
        '[padding-right:var(--grid-margin)] [padding-left:var(--grid-margin)]',
        densityVars,
        // Largura máxima do CONTEÚDO por faixa. Como a caixa é border-box, a
        // trava soma as duas margens — assim o conteúdo fica exatamente com
        // --grid-*-maxwidth, como o UIKIT desenha.
        !fluid && [
          '[--grid-maxwidth:var(--grid-portrait-maxwidth)]',
          'min-[576px]:[--grid-maxwidth:var(--grid-tablet-maxwidth)]',
          'min-[1280px]:[--grid-maxwidth:var(--grid-desktop-maxwidth)]',
          'min-[1600px]:[--grid-maxwidth:var(--grid-tv-maxwidth)]',
          '[max-width:calc(var(--grid-maxwidth)_+_2_*_var(--grid-margin))]',
        ],
        className
      )}
      {...props}
    />
  )
}

type RowProps = React.ComponentProps<'div'> & {
  /** Renderiza no elemento filho em vez de `<div>`. */
  asChild?: boolean
}

/**
 * Linha do grid: `display: grid` com o número de colunas e a calha da faixa
 * atual. Os filhos usam o utilitário nativo do Tailwind para o vão
 * (`col-span-*`) — lembrando que abaixo de 576px só existem 4 colunas e entre
 * 576px e 1279px só existem 8, então vãos maiores precisam ser responsivos
 * (ex.: `col-span-4 min-[1280px]:col-span-6`).
 *
 * Ao contrário do `.row` do core.css não usamos margem negativa + padding nas
 * colunas: `gap` faz o mesmo sem vazar para fora da linha. A ordem do DOM é a
 * ordem de leitura — nada de `order-*` aqui.
 */
function Row({ className, asChild = false, ...props }: RowProps) {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-slot="row"
      className={cn(
        'grid',
        densityVars,
        '[grid-template-columns:repeat(var(--grid-columns),minmax(0,1fr))]',
        '[column-gap:var(--grid-gutter)]',
        className
      )}
      {...props}
    />
  )
}

export { Container, Row }
export type { ContainerProps, RowProps }
